import { useEffect, useState } from 'react';
import { Button } from '@components/shared/Button/Button';
import { useFormContext } from '@context/FormContext';
import { useTexts } from 'utils/useTexts';
import { toDefaultPhoneFormat } from 'utils/stringFormat';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { usefetchContactDetailsByContactId } from '@services/contactDetails';
import OverlaySpinner from '@components/shared/OverlaySpinner/OverlaySpinner';
import { formatPostalCode, getContactData } from '@services/utils';
import { handleSoftPullLogic } from '@services/softPullHandler';

import { setLocalStorage } from 'utils/setLocalStorage';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

interface IUserDetails {
  firstName: string;
  lastName: string;
  streetLine1: string;
  city: string;
  state: string;
  zip: string;
  ssn: string | null;
  dateOfBirth: string;
  cellPhone: string;
  email: string;
}
export interface IBalanceInputs {
  currentMonthlyPayment: number;
}
export interface ISaveloanInfoPayload {
  currentMonthlyPayment: number;
  assetId: string;
  contactId: string;
  leadId: string;
}

const SoftPullAcknowledgementPage = () => {
  const { t } = useTexts();
  const contactData = getContactData();
  const { trackPage, startInteraction, trackClick, trackFormSubmit } = useAnalytics(SEGMENT_SCREEN_PARAMS.LOAN_DETAILS);
  const {
    currentStep,
    dispatch,
    createContactResponse,
    isLoading,
    submitStart,
    licensePlateInfoForm,
    manualLicensePlateInfoForm,
  } = useFormContext();
  const { next } = useScreenNavigator();

  const [userDetails, setUserDetails] = useState<IUserDetails>({} as IUserDetails);

  const handleSubmit = async () => {
    submitStart();
    startInteraction();
    trackClick('CTA Clicked', {
      cta_id: 'estimate_my_savings',
      cta_text: 'Estimate My Savings',
      cta_type: 'primary_button',
      cta_context: 'soft_pull_acknowledgement',
    });
    const currentMonthlyPaymentFromContext =
      licensePlateInfoForm?.currentMonthlyPayment || manualLicensePlateInfoForm?.currentMonthlyPayment || 0;
    const balanceAmountPayload: ISaveloanInfoPayload = {
      currentMonthlyPayment: currentMonthlyPaymentFromContext,
      assetId: contactData?.assetId,
      contactId: createContactResponseData?.contactId ?? '',
      leadId: createContactResponseData?.leadId ?? '',
    };
    const contactDataWithLoanBalance = {
      ...contactData,
      currentMonthlyPayment: currentMonthlyPaymentFromContext,
    };
    setLocalStorage(contactDataWithLoanBalance, 'contactData');
    submitStart();

    const softpullRequestPayload = {
      contactId: createContactResponseData?.contactId ?? '',
      leadId: createContactResponseData?.leadId ?? '',
    };

    await handleSoftPullLogic({
      softpullRequestPayload,
      balanceAmountPayload,
      currentStep,
      next,
      dispatch,
      trackFormSubmit,
    });
  };

  const createContactResponseData = !createContactResponse ? getContactData() || {} : createContactResponse;

  const { data: contactDetailsResponse, isValidating: isFetchContactLoading } = usefetchContactDetailsByContactId(
    contactData?.contactId
  );

  useEffectOnce(() => trackPage(t('pageTitles.SoftPullAcknowledgement')));

  useEffect(() => {
    if (!isFetchContactLoading && contactDetailsResponse) {
      const { firstName, lastName, ssn, dateOfBirth, cellPhone, email } = contactDetailsResponse;
      const { streetLine1, city, state, zip } = contactDetailsResponse?.addresses?.[0] ?? {
        streetLine1: '',
        city: '',
        state: '',
        zip: '',
      };
      setUserDetails({ firstName, lastName, streetLine1, city, state, zip, ssn, dateOfBirth, cellPhone, email });
    }
  }, [contactDetailsResponse, isFetchContactLoading]);

  const formatDateOfBirth = (dateOfBirth: string) => {
    const dob = dateOfBirth ? new Date(dateOfBirth) : '';
    return dob
      ? `${String(dob.getUTCMonth() + 1).padStart(2, '0')}/${String(dob.getUTCDate()).padStart(2, '0')}/${dob.getUTCFullYear()}`
      : '';
  };

  return (
    <>
      <div>
        {!createContactResponse && userDetails && !isFetchContactLoading && (
          <div className="iLendingCDX-SoftPull-InfoCard iLendingCDX-mb-4">
            <div className="iLendingCDX-SoftPull-InfoCard-container">
              <div className="iLendingCDX-SoftPull-InfoCard-info">
                <p>
                  {userDetails.firstName} {userDetails.lastName}
                </p>
                {contactDetailsResponse?.addresses?.[0] && (
                  <p>
                    {userDetails.streetLine1} {userDetails.city}, {userDetails.state} {formatPostalCode(userDetails.zip)}
                  </p>
                )}
                <p>{toDefaultPhoneFormat(userDetails.cellPhone)}</p>
                <p>{userDetails.ssn ?? ''}</p>
                <p>{formatDateOfBirth(userDetails.dateOfBirth)}</p>
                <p>{userDetails.email}</p>
              </div>
            </div>
          </div>
        )}
        <h1 className="iLendingCDX-form-title">{t('softPullAcknowledgement.title')}</h1>
        <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-6">{t('softPullAcknowledgement.description1')}</h2>
        <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-8">{t('softPullAcknowledgement.description2')}</h2>

        <Button
          type="button"
          className="iLendingCDX-button--rounded iLendingCDX-mb-4"
          size="lg"
          isLoading={isLoading}
          onClick={handleSubmit}
        >
          {t('softPullAcknowledgement.estimateMySavings')}
        </Button>

        <h2 className="iLendingCDX-softPullAcknowledge iLendingCDX-mb-4">{t('softPullAcknowledgement.acknowledge')}</h2>
      </div>
      {isFetchContactLoading && <OverlaySpinner size="lg" />}
    </>
  );
};

export default SoftPullAcknowledgementPage;
